import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Header from '../Header';
import './PlayerDashboard.css';

const PlayerDashboard = () => {
  const { currentUser, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const [myGames, setMyGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  // Delete-account flow state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Please enter your password to confirm.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount(deletePassword);
      // Account gone — auth state listener will clear the user and the
      // protected route redirects to /auth.
      navigate('/auth', { replace: true });
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setDeleteError('Incorrect password. Please try again.');
      } else if (code === 'auth/too-many-requests') {
        setDeleteError('Too many attempts. Please wait a moment and try again.');
      } else {
        setDeleteError(err?.message || 'Could not delete your account. Please try again.');
      }
      setDeleting(false);
    }
  };

  // Real-time listener for all games
  useEffect(() => {
    setLoading(true);
    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const gamesData = [];

      querySnapshot.forEach((doc) => {
        const gameData = { id: doc.id, ...doc.data() };

        // Only include games where user is in either team
        const inWhiteTeam = gameData.whiteTeam?.some(player => player.email === currentUser.email);
        const inBlackTeam = gameData.blackTeam?.some(player => player.email === currentUser.email);

        if (inWhiteTeam || inBlackTeam) {
          gamesData.push(gameData);
        }
      });

      setMyGames(gamesData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching games:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleJoinGame = () => {
    if (!inviteCode.trim()) {
      alert('Please enter an invite code');
      return;
    }

    // Navigate to join page with the code
    navigate(`/join/${inviteCode.toUpperCase().trim()}`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      waiting: { text: 'Waiting', class: 'status-waiting' },
      in_progress: { text: 'In Progress', class: 'status-active' },
      completed: { text: 'Completed', class: 'status-completed' }
    };
    return badges[status] || badges.waiting;
  };

  const getMyTeam = (game) => {
    const inWhiteTeam = game.whiteTeam?.some(player => player.email === currentUser.email);
    const inBlackTeam = game.blackTeam?.some(player => player.email === currentUser.email);

    if (inWhiteTeam) return 'White';
    if (inBlackTeam) return 'Black';
    return 'Spectator';
  };

  const getMyPlayerData = (game) => {
    const whitePlayer = game.whiteTeam?.find(player => player.email === currentUser.email);
    if (whitePlayer) return whitePlayer;

    const blackPlayer = game.blackTeam?.find(player => player.email === currentUser.email);
    if (blackPlayer) return blackPlayer;

    return null;
  };

  return (
    <div className="player-container">
      <Header />

      <div className="player-content">
        <div className="content-header">
          <h2 className="section-title">My Games</h2>
          <button
            onClick={() => setShowJoinModal(true)}
            className="join-game-button"
          >
            + Join Game
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your games...</p>
          </div>
        ) : myGames.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">♟</div>
            <h3>No games yet</h3>
            <p>Join a game using an invite code to get started</p>
            <button
              onClick={() => setShowJoinModal(true)}
              className="empty-join-button"
            >
              Join Your First Game
            </button>
          </div>
        ) : (
          <div className="games-grid">
            {myGames.map((game) => {
              const statusInfo = getStatusBadge(game.status);
              const myTeam = getMyTeam(game);
              const myPlayerData = getMyPlayerData(game);

              return (
                <div key={game.id} className="game-card">
                  <div className="game-card-header">
                    <h3 className="game-name">{game.name}</h3>
                    <span className={`status-badge ${statusInfo.class}`}>
                      {statusInfo.text}
                    </span>
                  </div>

                  <div className="my-role">
                    <span className="role-label">Playing as:</span>
                    <span className={`role-value ${myTeam.toLowerCase()}`}>
                      {myTeam === 'White' ? '♔' : '♚'} {myPlayerData?.nickname || currentUser.email}
                    </span>
                    <span className="team-badge">({myTeam} Team)</span>
                  </div>

                  {myPlayerData?.role && (
                    <div className="my-pieces">
                      <span className="pieces-label">My Pieces:</span>
                      <span className="pieces-value">{myPlayerData.role}</span>
                    </div>
                  )}

                  <div className="game-info">
                    <div className="info-row">
                      <span className="info-label">White Team:</span>
                      <span className="info-value">
                        {game.whiteTeam?.length || 0} players
                      </span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Black Team:</span>
                      <span className="info-value">
                        {game.blackTeam?.length || 0} players
                      </span>
                    </div>

                    <div className="info-row">
                      <span className="info-label">Invite Code:</span>
                      <span className="invite-code-small">{game.inviteCode}</span>
                    </div>
                  </div>

                  <div className="game-actions">
                    <button
                      onClick={() => navigate(`/game/${game.id}`)}
                      className="action-button play-button"
                    >
                      {game.status === 'in_progress' ? 'Play Game' : 'View Game'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Account footer: privacy link + account deletion */}
        <div className="account-footer">
          <Link to="/privacy" className="account-footer-link">Privacy Policy</Link>
          <span className="account-footer-sep">·</span>
          <button
            type="button"
            className="delete-account-link"
            onClick={() => setShowDeleteModal(true)}
          >
            Delete my account
          </button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete your account</h2>
              <button className="close-button" onClick={closeDeleteModal} disabled={deleting}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                This permanently deletes your account and profile from Living Chess. This action
                cannot be undone. Enter your password to confirm.
              </p>

              {deleteError && (
                <div className="error-message" style={{ marginBottom: '12px' }}>
                  <span className="error-icon">⚠</span> {deleteError}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="deletePassword">Password</label>
                <input
                  type="password"
                  id="deletePassword"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  autoFocus
                  disabled={deleting}
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={closeDeleteModal}
                  className="cancel-button"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  className="danger-button"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting…' : 'Delete account'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Join Game Modal */}
      {showJoinModal && (
        <div className="modal-overlay" onClick={() => setShowJoinModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Join a Game</h2>
              <button
                className="close-button"
                onClick={() => setShowJoinModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Enter the 6-character invite code you received from the game host.
              </p>
              <div className="form-group">
                <label htmlFor="inviteCode">Invite Code</label>
                <input
                  type="text"
                  id="inviteCode"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  placeholder="e.g., XXYHZ9"
                  maxLength={6}
                  autoFocus
                  style={{
                    textTransform: 'uppercase',
                    letterSpacing: '3px',
                    fontSize: '18px',
                    textAlign: 'center'
                  }}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setShowJoinModal(false)}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleJoinGame}
                  className="submit-button"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDashboard;
